import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { useCustomerAnalytics } from "../../../services/hooks/useCustomerAnalytics";
import {
  useAppDispatch,
  useAppSelector,
} from "../../../services/hooks/useReduxHooks";
import { setBottomSheetState } from "../../../services/redux/slices/globalComponentsToggleSlice";
import { CUSTOMER_ANALYTICS_EVENT } from "../../../utils/enums";
import { isOnlyOneOrderTypePerFranchise } from "../../../utils/help/orderType/orderType";
import { INVERTED_SECTIONS } from "../../../utils/types";
import styles from "./BottomSheet.module.scss";

export default function BottomSheet(props: {
  children: React.ReactNode;
  displayedSection?: INVERTED_SECTIONS;
}) {
  const dispatch = useAppDispatch();
  const addCustomerAnalytics = useCustomerAnalytics();
  const modalSheetContent = useRef<HTMLDivElement>(null);
  const bottomSheetModal = useAppSelector(
    (state) => state.globalComponentsToggleSlice.bottomSheetModal
  );

  const [disableClickOutside, setDisableClickOutside] = useState(false);

  // NOT scroll-locked, deliberately. Locking the page (added for the undimmed
  // strip below the scrim) removed the one thing that made a cropped sheet
  // recoverable: scrolling retracts the browser's chrome, which grows the
  // visible viewport and brings the CTAs back on screen. With the page pinned
  // that escape hatch is gone and a sheet whose footer lands behind the toolbar
  // stays unusable, so customers could not order at all. The undimmed strip is
  // cosmetic; that is not. See prepits/project#4233 — the real fix is to size
  // the scrim from visualViewport instead of relying on either.

  const activeFranchises = useAppSelector(
    (state) => state.config.activeFranchises
  );
  const restaurantInfo = useAppSelector((state) => state.config.restaurantInfo);

  useEffect(() => {
    if (activeFranchises.length === 1) {
      const franchise = activeFranchises[0];

      const shouldDisableClickOutside = isOnlyOneOrderTypePerFranchise(
        franchise.franchiseConfig
      );

      setDisableClickOutside(shouldDisableClickOutside);
    }
  }, [activeFranchises]);

  useLayoutEffect(() => {
    if (modalSheetContent.current) {
      if (bottomSheetModal.isOpen) {
        modalSheetContent.current.classList.add(
          styles.bottom_sheet_content__open
        );
        modalSheetContent.current.classList.remove(
          styles.bottom_sheet_content__closed
        );
      } else {
        modalSheetContent.current.classList.add(
          styles.bottom_sheet_content__closed
        );
        modalSheetContent.current.classList.remove(
          styles.bottom_sheet_content__open
        );
      }
    }
  }, [bottomSheetModal.isOpen]);

  const handleClose = () => {
    if (disableClickOutside) {
      return;
    }
    // trigger event welcome_drawer_skipped when i click outside the welcome drawer
    if (props.displayedSection === INVERTED_SECTIONS.SELECT_ORDER_INFO) {
      addCustomerAnalytics(CUSTOMER_ANALYTICS_EVENT.WELCOME_DRAWER_SKIPPED);
    }

    dispatch(setBottomSheetState(false));
  };

  return (
    <div
      className={styles.bottom_sheet}
      data-testid="bottom-sheet-overlay"
      onClick={handleClose}
    >
      <div
        className={`${styles.bottom_sheet_content} ${
          props.displayedSection === INVERTED_SECTIONS.SELECT_ORDER_INFO
            ? styles.bottom_sheet_content_welcome
            : ""
        } ${
          restaurantInfo?.colors?.invertedSections?.includes(
            props.displayedSection || ("" as INVERTED_SECTIONS)
          )
            ? "invert"
            : ""
        }`}
        onClick={(e) => e.stopPropagation()}
        ref={modalSheetContent}
      >
        {props.children}
      </div>
    </div>
  );
}
